import type { InfiniteData, Query, QueryFilters } from '@tanstack/react-query'

import { getFeedKey } from '../../utils/keys/feed.keys.js'
import { getPostsKey } from '../../utils/keys/post.key.js'
import { getPinnedPostsKey } from '../../utils/keys/space.key.js'

// get all active/inactive posts queries
export const postsQueryFilter: QueryFilters = {
  predicate: query => {
    const { state, queryKey } = query
    const [main] = queryKey || []
    if (
      (main === getFeedKey()[0] || main === getPostsKey()[0]) &&
      Array.isArray((state?.data as InfiniteData<unknown>)?.pages) // checking if the disabled(inactive) query actually has data to update or not
    ) {
      return true
    }
    return false
  },
}

type PostListVariables = {
  spaceIds?: string[]
  postId?: string
  memberId?: string
  tagId?: string
  tagSlug?: string
  query?: string
  filterBy?: unknown[]
}

const getVariables = (query: Query): PostListVariables => {
  const [, args] = query.queryKey || []
  return (args as { variables?: PostListVariables })?.variables ?? {}
}

const matchesSpacePosts = (query: Query, spaceId: string): boolean => {
  const [main] = query.queryKey || []

  if (main === getFeedKey()[0]) {
    return true
  }

  if (main !== getPostsKey()[0]) {
    return false
  }

  const { spaceIds, postId, memberId, tagId, tagSlug } = getVariables(query)

  // `posts` is a shared key: a published post is never a reply, can't be proven
  // to carry a tag, and belongs only on its author's member list — which
  // `useAddPost` invalidates by member id.
  if (postId || memberId || tagId || tagSlug) {
    return false
  }

  // No `spaceIds` means a site-wide query, which any space's posts belong in.
  return !spaceIds?.length || spaceIds.includes(spaceId)
}

// Search and filter constraints can't be evaluated client-side, so the new post
// can't be proven to belong. Those queries rely on invalidation instead.
const acceptsOptimisticPost = (query: Query): boolean => {
  const { query: search, filterBy } = getVariables(query)

  return !search && !filterBy?.length
}

export const getSpacePostsQueryFilter: (
  spaceId: string,
) => QueryFilters = spaceId => ({
  predicate: query => matchesSpacePosts(query, spaceId),
})

// Without the data guard, `addPostToPaginatedPost` fabricates a single-post
// page for a query that never fetched.
export const getSpacePostsDataQueryFilter: (
  spaceId: string,
) => QueryFilters = spaceId => ({
  predicate: query =>
    matchesSpacePosts(query, spaceId) &&
    acceptsOptimisticPost(query) &&
    Array.isArray((query.state?.data as InfiniteData<unknown>)?.pages),
})

export const pinnedPostsFilter: QueryFilters = {
  predicate: query => {
    const queryMainKey = query?.queryKey?.[0]
    if (queryMainKey === getPinnedPostsKey()[0]) {
      return true
    }
    return false
  },
}

// to get one replies/posts query with a specific id
export const getPostsQueryById: (postId: string) => QueryFilters = postId => ({
  predicate: query => {
    const { state, queryKey } = query
    const [main, args] = queryKey || []
    if (
      main === getPostsKey()[0] &&
      (args as { variables?: { postId?: string } })?.variables?.postId ===
        postId &&
      Array.isArray((state?.data as InfiniteData<unknown>)?.pages) // checking if the disabled(inactive) query actually has data to update or not
    ) {
      return true
    }
    return false
  },
})
